import React, { memo, useCallback, useEffect, useState } from 'react';
import { areEqual } from 'Utils/equalityChecks';

import { getUnassignedCosts } from '../actions';
import type { CostLine, PaginationMeta } from '../types';
import { COST_SOURCE_LABELS } from '../types';
import { formatLineAmount, formatDate } from '../format';
import { Pagination } from '../Pagination';
import classes from '../jobCosting.module.css';

interface Props {
  ssoToken: string;
}

const EMPTY_META: PaginationMeta = { currentPage: 1, lastPage: 1, perPage: 100, total: 0 };

function JobCostingUnassigned({ ssoToken }: Props) {
  const [lines, setLines] = useState<CostLine[]>([]);
  const [meta, setMeta] = useState<PaginationMeta>(EMPTY_META);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getUnassignedCosts(ssoToken, page);
      setLines(result.data);
      setMeta(result.meta);
    } catch {
      setError('Unable to load unassigned costs.');
    } finally {
      setLoading(false);
    }
  }, [ssoToken, page]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <div className={classes.loadingState}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return <div className={`alert alert-danger ${classes.emptyState}`}>{error}</div>;
  }

  if (lines.length === 0) {
    return (
      <div className={classes.emptyState}>
        <p>No unassigned costs.</p>
        <p className="text-muted">
          Costs that can&apos;t be matched to a job will appear here once QuickBooks cost sync is
          enabled.
        </p>
      </div>
    );
  }

  return (
    <>
      <div className={classes.tableWrapper}>
        <table className={classes.table}>
          <thead>
            <tr>
              <th>Date</th>
              <th>Source</th>
              <th>Vendor</th>
              <th>Description</th>
              <th>QBO Txn Type</th>
              <th className={classes.num}>Amount</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((l) => (
              <tr key={l.id}>
                <td>{formatDate(l.txnDate)}</td>
                <td>{COST_SOURCE_LABELS[l.source as keyof typeof COST_SOURCE_LABELS] || l.source}</td>
                <td>{l.vendorName || '—'}</td>
                <td>{l.description || '—'}</td>
                <td>{l.quickbooksTxnType || '—'}</td>
                <td className={classes.num}>{formatLineAmount(l.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Pagination meta={meta} onPageChange={setPage} />
    </>
  );
}

const JobCostingUnassignedMemo = memo(JobCostingUnassigned, areEqual);

export { JobCostingUnassignedMemo as JobCostingUnassigned };
